import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { DeviceChannel, Severity, UpdateDeviceChannelRequest } from "@/contracts";

import {
  controlDeviceChannelMutation,
  updateDeviceChannelMutation,
} from "@/api/queries";
import { useApiClient } from "@/lib/api-context";
import { Button } from "@/components/ui/button";
import { FormDialog } from "@/components/ui/form-dialog";
import { SelectField, TextField } from "@/components/ui/field";
import { CHANNEL_KIND_LABELS } from "./device-labels";

const SEVERITIES: Severity[] = ["INFO", "WARNING", "CRITICAL"];

export function ChannelConfigDialog({
  deviceId,
  channel,
  open,
  onClose,
  canControl,
}: {
  deviceId: string;
  channel: DeviceChannel | null;
  open: boolean;
  onClose: () => void;
  canControl: boolean;
}) {
  if (!channel) {
    return null;
  }

  // Remount when the channel changes so form state initialises from props
  // without a syncing effect (react-hooks/set-state-in-effect).
  return (
    <ChannelConfigForm
      key={channel.id}
      deviceId={deviceId}
      channel={channel}
      open={open}
      onClose={onClose}
      canControl={canControl}
    />
  );
}

function ChannelConfigForm({
  deviceId,
  channel,
  open,
  onClose,
  canControl,
}: {
  deviceId: string;
  channel: DeviceChannel;
  open: boolean;
  onClose: () => void;
  canControl: boolean;
}) {
  const { t: tDevices } = useTranslation("devices");
  const { t } = useTranslation("common");
  const client = useApiClient();
  const queryClient = useQueryClient();

  const [label, setLabel] = useState(channel.label ?? "");
  const [unit, setUnit] = useState(channel.unit ?? "");
  const [alertEnabled, setAlertEnabled] = useState(channel.alertEnabled);
  const [alertMin, setAlertMin] = useState(
    channel.alertMin === null ? "" : String(channel.alertMin),
  );
  const [alertMax, setAlertMax] = useState(
    channel.alertMax === null ? "" : String(channel.alertMax),
  );
  const [alertFaultHigh, setAlertFaultHigh] = useState(channel.alertFaultHigh);
  const [alertDurationSeconds, setAlertDurationSeconds] = useState(
    String(channel.alertDurationSeconds),
  );
  const [alertCooldownSeconds, setAlertCooldownSeconds] = useState(
    String(channel.alertCooldownSeconds),
  );
  const [alertSeverity, setAlertSeverity] = useState<Severity>(channel.alertSeverity);
  const [controlMessage, setControlMessage] = useState<string | null>(null);

  const save = useMutation({
    ...updateDeviceChannelMutation(client, deviceId, channel.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      onClose();
    },
  });

  const control = useMutation({
    ...controlDeviceChannelMutation(client, deviceId, channel.id),
    onSuccess: (result) => {
      setControlMessage(
        result.queued
          ? `${result.downlinkHex} queued.`
          : `${result.downlinkHex} — ${result.detail ?? "not queued"}`,
      );
    },
  });

  const isDigital = channel.kind === "DIGITAL" || channel.kind === "DIGITAL_OUTPUT";
  const isOutput = channel.kind === "DIGITAL_OUTPUT";

  function buildBody(): UpdateDeviceChannelRequest {
    const body: UpdateDeviceChannelRequest = {
      label: label.trim() === "" ? null : label.trim(),
      unit: unit.trim() === "" ? null : unit.trim(),
      alertEnabled,
      alertFaultHigh,
      alertDurationSeconds: Number(alertDurationSeconds),
      alertCooldownSeconds: Number(alertCooldownSeconds),
      alertSeverity,
    };

    if (!isDigital) {
      body.alertMin = alertMin.trim() === "" ? null : Number(alertMin);
      body.alertMax = alertMax.trim() === "" ? null : Number(alertMax);
    }

    return body;
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(buildBody());
  }

  return (
    <FormDialog
      open={open}
      title={tDevices("channels.editTitle")}
      description={`${channel.key} · ${CHANNEL_KIND_LABELS[channel.kind]}`}
      submitLabel={t("save")}
      pending={save.isPending}
      onSubmit={onSubmit}
      onCancel={() => {
        if (!save.isPending) {
          onClose();
        }
      }}
    >
      <div className="flex flex-col gap-3">
        <TextField
          label={tDevices("channels.label")}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        <TextField
          label={tDevices("channels.unit")}
          value={unit}
          onChange={(event) => setUnit(event.target.value)}
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={alertEnabled}
            onChange={(event) => setAlertEnabled(event.target.checked)}
          />
          {tDevices("channels.alertEnabled")}
        </label>

        {isDigital ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={alertFaultHigh}
              onChange={(event) => setAlertFaultHigh(event.target.checked)}
            />
            {tDevices("channels.alertFaultHigh")}
          </label>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <TextField
              label={tDevices("channels.alertMin")}
              value={alertMin}
              onChange={(event) => setAlertMin(event.target.value)}
              inputMode="decimal"
            />
            <TextField
              label={tDevices("channels.alertMax")}
              value={alertMax}
              onChange={(event) => setAlertMax(event.target.value)}
              inputMode="decimal"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <TextField
            label={tDevices("channels.alertDuration")}
            value={alertDurationSeconds}
            onChange={(event) => setAlertDurationSeconds(event.target.value)}
            inputMode="numeric"
          />
          <TextField
            label={tDevices("channels.alertCooldown")}
            value={alertCooldownSeconds}
            onChange={(event) => setAlertCooldownSeconds(event.target.value)}
            inputMode="numeric"
          />
        </div>

        <SelectField
          label={tDevices("channels.alertSeverity")}
          value={alertSeverity}
          onChange={(value) => setAlertSeverity(value as Severity)}
          data={SEVERITIES.map((severity) => ({
            value: severity,
            label: tDevices(`severity.${severity}`),
          }))}
        />

        {save.isError ? (
          <p className="text-sm font-medium text-danger">{save.error.message}</p>
        ) : null}

        {canControl && isOutput ? (
          <div className="mt-2 border-t border-border-subtle pt-3">
            <p className="mb-2 text-sm text-ink-muted">{tDevices("channels.controlHint")}</p>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={control.isPending}
                onClick={() => control.mutate({ state: "on" })}
              >
                {tDevices("channels.controlOn")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={control.isPending}
                onClick={() => control.mutate({ state: "off" })}
              >
                {tDevices("channels.controlOff")}
              </Button>
            </div>
            {controlMessage ? <p className="mt-2 font-mono text-xs">{controlMessage}</p> : null}
            {control.isError ? (
              <p className="mt-2 text-sm font-medium text-danger">{control.error.message}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </FormDialog>
  );
}
